'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteProcessors } from '@/lib/api/processors'
import { useToast } from '@/components/ui/use-toast'

interface BulkDeleteProcessorsModalProps {
    processorIds: string[]
    onClose: () => void
    onSuccess?: () => void
}

/**
 * Modal component that confirms bulk processor deletion (archiving)
 */
export const BulkDeleteProcessorsModal = ({ processorIds, onClose, onSuccess }: BulkDeleteProcessorsModalProps) => {
    const { t } = useTranslation('processors')
    const { t: tCommon } = useTranslation('common')
    const { toast } = useToast()
    const deleteProcessors = useDeleteProcessors()

    const count = processorIds.length

    const handleDelete = async () => {
        if (processorIds.length === 0) return

        try {
            await deleteProcessors.mutateAsync({ processorIds })

            toast({
                title: t('bulkDeleteSuccess'),
                description: t('processorsDeleted', { count }),
            })

            onSuccess?.()
            onClose()
        } catch (error) {
            toast({
                title: tCommon('error'),
                description: t('bulkDeleteError'),
                variant: 'destructive',
            })
        }
    }

    return (
        <AlertDialog open={true} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('bulkDeleteTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('bulkDeleteConfirmation', { count })}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={deleteProcessors.isPending}>
                        {tCommon('cancel')}
                    </Button>
                    <Button variant="destructive" onClick={handleDelete} disabled={deleteProcessors.isPending}>
                        {deleteProcessors.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {tCommon('delete')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

